/**
 * custom-cart-progressbar.js
 *
 * Cart mirror of the progress bar section (custom-progressbar.liquid).
 * Reads configuration published by the section into window.PB2Config and
 * updates the bar DOM whenever the cart total changes.
 *
 * Key difference from the old inline snippet script:
 *   - Listens to `custom:cart:updated` (from CartManager) for real-time updates.
 *     No extra /cart.js fetch on every cart action — uses event.detail.cart directly.
 *   - One initial /cart.js fetch on page load to render the correct initial state.
 *   - State-filter changes (localStorage) re-render using the cached cart total.
 *
 * Dependencies:
 *   - window.PB2Config published by custom-progressbar.liquid section script.
 *   - custom-cart-manager.js must dispatch `custom:cart:updated` events.
 */

(function () {
  // Guard: skip entirely when the cart progress bar isn't rendered on this page
  // (e.g. settings.show_brindes_cart disabled) — avoids retrying for PB2Config
  // and warning in the console when there's nothing to update.
  if (!document.getElementById('pb2cart-root')) return;

  // Guard: only initialise once per page, even if the snippet is re-rendered
  // (cart-items-component morphs the DOM but does not re-execute <script type="module">)
  if (window._pb2CartInit) {
    // Re-render happened — just refresh the bar with the last known total
    if (window._pb2CartLastTotal !== undefined) {
      _updateBar(window._pb2CartLastTotal);
    }
    return;
  }
  window._pb2CartInit = true;

  let _cfg          = null;
  let _lastTotal    = 0;
  let _s1Reached    = false;
  let _s2Reached    = false;
  const SKELETON_MS = 600;
  const _shownAt    = Date.now();

  // ── Config lookup ─────────────────────────────────────────────────────────

  function _findConfig() {
    const pb2 = window.PB2Config;
    if (!pb2) return null;
    const sid  = pb2._latest;
    if (sid && pb2[sid]) return pb2[sid];
    const keys = Object.keys(pb2).filter(k => k !== '_latest');
    return keys.length ? pb2[keys[0]] : null;
  }

  // ── DOM helpers ───────────────────────────────────────────────────────────

  /**
   * Re-query scoped to a specific root element to survive morph and to
   * support multiple cart-drawer instances (desktop + mobile).
   */
  function _els(root) {
    return {
      root,
      text:    root.querySelector('#pb2cart-text'),
      fill:    root.querySelector('#pb2cart-fill'),
      track:   root.querySelector('#pb2cart-track'),
      marker1: root.querySelector('#pb2cart-marker1'),
      marker2: root.querySelector('#pb2cart-marker2'),
    };
  }

  function _ensureMarker(el, defaultSvg, reachedSvg) {
    if (!el || el.children.length > 0) return;
    el.innerHTML =
      `<div class="pb2cart__marker-icon pb2cart__marker-icon--default">${defaultSvg}</div>` +
      `<div class="pb2cart__marker-icon pb2cart__marker-icon--reached">${reachedSvg}</div>`;
  }

  function _formatCurrency(cents) {
    return 'R$\u00a0' + (cents / 100).toFixed(2).replace('.', ',');
  }

  function _formatText(template, cents) {
    return template.replace(/\{valor\}/g, `<b>${_formatCurrency(cents)}</b>`);
  }

  function _triggerPulse(el) {
    el.classList.remove('pulse');
    void el.offsetWidth; // reflow to restart animation
    el.classList.add('pulse');
    el.addEventListener('animationend', function handler() {
      el.classList.remove('pulse');
      el.removeEventListener('animationend', handler);
    });
  }

  // ── Core update ───────────────────────────────────────────────────────────

  /**
   * Updates every #pb2cart-root instance in the document.
   * There are multiple instances because header-actions is rendered 3 times
   * (desktop right, mobile top, mobile menu). Only iterating all of them
   * ensures the visible drawer — whichever breakpoint the user is on —
   * always reflects the current cart state.
   */
  function _updateBar(totalCents) {
    _cfg = _cfg || _findConfig();
    if (!_cfg) return;

    _lastTotal               = totalCents;
    window._pb2CartLastTotal = totalCents; // exposed for re-render guard

    const roots = document.querySelectorAll('#pb2cart-root');
    if (!roots.length) return;

    roots.forEach(root => _applyToRoot(root, totalCents));
  }

  function _applyToRoot(root, totalCents) {
    const e = _els(root);

    _ensureMarker(e.marker1, _cfg.s1Svg, _cfg.s1SvgReached);
    _ensureMarker(e.marker2, _cfg.s2Svg, _cfg.s2SvgReached);

    const state = localStorage.getItem(_cfg.storageKey) || _cfg.defaultState || 'SP';
    const t1    = _cfg.enableStateFilter ? (_cfg.t1ByState[state] || 0) : _cfg.t1Global;
    const t2    = _cfg.enableStages
      ? (_cfg.enableStateFilter ? (_cfg.t2ByState[state] || 0) : _cfg.t2Global)
      : t1;

    const maxThreshold = t2 || t1 || 1;
    const fillPct      = Math.min((totalCents / maxThreshold) * 100, 100);
    const stage1       = totalCents >= t1;
    const stage2       = totalCents >= t2;

    // Marker 1 position
    if (e.marker1) {
      if (_cfg.enableStages && t2 > 0) {
        e.marker1.style.left    = ((t1 / t2) * 100).toFixed(2) + '%';
        e.marker1.style.display = '';
      } else {
        e.marker1.style.display = 'none';
      }
    }

    // Fill
    e.fill.style.width           = fillPct.toFixed(2) + '%';
    e.track.setAttribute('aria-valuenow', fillPct.toFixed(2));

    const style           = getComputedStyle(e.root);
    const cFill           = style.getPropertyValue('--pb2cart-fill').trim();
    const cFillReached    = style.getPropertyValue('--pb2cart-fill-reached').trim();
    const cBg             = style.getPropertyValue('--pb2cart-bg-progress').trim();
    const cText           = style.getPropertyValue('--pb2cart-text').trim();
    const cTextComplete   = style.getPropertyValue('--pb2cart-text-complete').trim();

    e.fill.style.backgroundColor  = stage2 ? cFillReached : cFill;
    e.track.style.backgroundColor = cBg;

    // Marker 1
    if (e.marker1 && _cfg.enableStages) {
      if (stage1) {
        if (!e.marker1.classList.contains('is-reached')) _triggerPulse(e.marker1);
        e.marker1.classList.add('is-reached');
        e.marker1.style.backgroundColor = cFillReached;
        e.marker1.style.borderColor     = cFillReached;
      } else {
        e.marker1.classList.remove('is-reached');
        e.marker1.style.backgroundColor = cBg;
        e.marker1.style.borderColor     = cFill;
      }
    }

    // Marker 2
    if (e.marker2) {
      if (stage2) {
        if (!e.marker2.classList.contains('is-reached')) _triggerPulse(e.marker2);
        e.marker2.classList.add('is-reached');
        e.marker2.style.backgroundColor = cFillReached;
        e.marker2.style.borderColor     = cFillReached;
      } else {
        e.marker2.classList.remove('is-reached');
        e.marker2.style.backgroundColor = cBg;
        e.marker2.style.borderColor     = cFill;
      }
    }

    _s1Reached = stage1;
    _s2Reached = stage2;

    // Text
    const TEXT_BEFORE_1 = 'Adicione {valor} para desbloquear o primeiro benefício';
    const TEXT_BEFORE_2 = 'Adicione {valor} para desbloquear mais um benefício';
    const TEXT_COMPLETE = 'Você desbloqueou todos os benefícios!';

    const remaining1 = Math.max(t1 - totalCents, 0);
    const remaining2 = Math.max(t2 - totalCents, 0);
    let   html;

    if (!stage1) {
      html = _formatText(TEXT_BEFORE_1, remaining1);
    } else if (_cfg.enableStages && !stage2) {
      html = _formatText(TEXT_BEFORE_2, remaining2);
    } else {
      html = TEXT_COMPLETE;
    }

    e.text.innerHTML   = html;
    e.text.style.color = stage2 ? cTextComplete : cText;
    e.text.classList.toggle('is-complete', stage2);

    // Reveal real bar — respect minimum skeleton display time
    const skeleton = root.querySelector('#pb2cart-skeleton');
    const wrapper  = root.querySelector('#pb2cart-wrapper');
    const elapsed  = Date.now() - _shownAt;
    const delay    = Math.max(0, SKELETON_MS - elapsed);

    setTimeout(() => {
      if (skeleton) skeleton.classList.add('is-hidden');
      if (wrapper) {
        wrapper.style.opacity      = '1';
        wrapper.style.pointerEvents = '';
      }
    }, delay);
  }

  // ── Initialisation ────────────────────────────────────────────────────────

  // Register custom:cart:updated IMMEDIATELY — before _init() finds PB2Config —
  // so a CartManager dispatch on DOMContentLoaded is never missed.
  // If config isn't ready yet, cache the total and apply it once _init() completes.
  let _pendingTotal = null;

  document.addEventListener('custom:cart:updated', event => {
    const total = event.detail?.cart?.total_price;
    if (total === undefined) return;
    if (_cfg) {
      _updateBar(total);
    } else {
      _pendingTotal = total; // config not ready yet — store for _init() to consume
    }
  });

  // State-filter change (user changed region in top bar)
  window.addEventListener('storage', e => {
    if (_cfg && e.key === _cfg.storageKey) _updateBar(_lastTotal);
  });

  let _attempts = 0;

  function _init() {
    _cfg = _findConfig();
    if (!_cfg) {
      if (++_attempts < 60) { setTimeout(_init, 50); return; }
      console.warn('[CartProgressbar] PB2Config not found after retries.');
      return;
    }

    // If custom:cart:updated already arrived while we were waiting for config,
    // use that total — no extra fetch needed.
    if (_pendingTotal !== null) {
      _updateBar(_pendingTotal);
      _pendingTotal = null;
      return;
    }

    // Initial render — one fetch per page load
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(r => r.json())
      .then(cart => _updateBar(cart.total_price))
      .catch(() => {});
  }

  // ── Fallback: re-initialize when any cart drawer opens ───────────────────
  // Handles mobile edge-cases where the initial fetch ran before #pb2cart-root
  // existed in the DOM (e.g. cart empty on load, or script executed before the
  // CartManager DOMContentLoaded event fired).
  // Must listen on ALL cart-drawer-component instances: header-actions is
  // rendered 3 times (desktop, mobile top, mobile menu), so we listen to each.
  function _hookCartDrawerOpen() {
    document.querySelectorAll('cart-drawer-component').forEach(drawer => {
      drawer.addEventListener('dialog:open', () => {
        // Check if any pb2cart-skeleton is still visible (not yet initialized).
        const anyNotReady = [...document.querySelectorAll('#pb2cart-skeleton')]
          .some(s => !s.classList.contains('is-hidden'));
        if (!anyNotReady) return;
        if (_cfg) {
          _updateBar(_lastTotal);
        } else {
          // Config still not found — reset retry counter and try again.
          _attempts = 0;
          _init();
        }
      });
    });
  }
  _hookCartDrawerOpen();

  _init();
})();
